import { Box } from '@mantine/core';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

const pulseAnimation = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
  }
`;

const pulseRingAnimation = `
  @keyframes pulseRing {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.5); opacity: 0; }
  }
`;

// Inject keyframes into document
if (typeof document !== 'undefined') {
  const styleId = 'pulse-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = pulseAnimation + pulseRingAnimation;
    document.head.appendChild(style);
  }
}

export interface PulseProps {
  children: React.ReactNode;
  active?: boolean;
  duration?: number;
  intensity?: 'subtle' | 'medium' | 'strong';
  variant?: 'scale' | 'ring' | 'glow';
  color?: string;
  infinite?: boolean;
  count?: number;
}

const intensityValues = {
  subtle: { scale: 1.02, opacity: 0.9 },
  medium: { scale: 1.05, opacity: 0.8 },
  strong: { scale: 1.1, opacity: 0.7 },
};

const Pulse = forwardRef<HTMLDivElement, PulseProps>(
  (
    {
      children,
      active = true,
      duration = 1000,
      intensity = 'medium',
      variant = 'scale',
      color,
      infinite = true,
      count = 1,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();

    if (reducedMotion || !active) {
      return <Box ref={ref}>{children}</Box>;
    }

    const iterationCount = infinite ? 'infinite' : count;
    const values = intensityValues[intensity];

    if (variant === 'ring') {
      return (
        <Box ref={ref} pos="relative" style={{ display: 'inline-block' }}>
          {children}
          <Box
            pos="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            style={{
              borderRadius: 'inherit',
              border: `2px solid ${color || 'currentColor'}`,
              animation: `pulseRing ${duration}ms ease-out ${iterationCount}`,
              pointerEvents: 'none',
            }}
          />
        </Box>
      );
    }

    if (variant === 'glow') {
      return (
        <Box
          ref={ref}
          style={{
            animation: `pulse ${duration}ms ease-in-out ${iterationCount}`,
            filter: `drop-shadow(0 0 ${intensity === 'strong' ? '10px' : intensity === 'medium' ? '6px' : '3px'} ${color || 'currentColor'})`,
          }}
        >
          {children}
        </Box>
      );
    }

    return (
      <Box
        ref={ref}
        style={{
          animation: `pulse ${duration}ms ease-in-out ${iterationCount}`,
        }}
      >
        {children}
      </Box>
    );
  }
);

Pulse.displayName = 'Pulse';

export default Pulse;
